import { DisabledWithTooltip } from "@/flokit/utils/disabled-with-tooltip/disabled-with-tooltip";
import { DisabledWithTooltipWrapper } from "@/flokit/utils/disabled-with-tooltip/disabled-with-tooltip-wrapper";
import { normalizeDisabledWithTooltip } from "@/flokit/utils/disabled-with-tooltip/normalize-disabled-with-tooltip";
import { cx, VariantProps } from "class-variance-authority";
import Link from "next/link";
import React, { AnchorHTMLAttributes } from "react";
import { buttonStyles } from "./styles";

export interface FloKitLinkButtonProps
  extends VariantProps<typeof buttonStyles>,
    Omit<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      "type" | "size" | "color" | "className"
    > {
  children: React.ReactNode;
  onConfirm?: () => void;
  isPlayAppLink?: boolean;
  href: string;
  disabled?: DisabledWithTooltip;
  shouldOpenInNewTab?: boolean;
  UNSAFE_className?: string;

  /**
   * Use shouldOpenInNewTab instead
   */
  target?: never;
  // TODO: Loading prop
}

export const LinkButton = React.forwardRef(
  (
    {
      children,
      color,
      size,
      width,
      onConfirm,
      isPlayAppLink,
      href,
      wrapText,
      disabled,
      shouldOpenInNewTab,
      // eslint-disable-next-line camelcase
      UNSAFE_className,
      ...props
    }: FloKitLinkButtonProps,
    ref: React.Ref<HTMLAnchorElement>,
  ) => {
    const disabledWithTooltip = normalizeDisabledWithTooltip(disabled);

    const shouldOpenInNewTabAttributes = shouldOpenInNewTab
      ? openInNewTabHtmlAttributes
      : {};

    return disabledWithTooltip.isDisabled ? (
      <DisabledWithTooltipWrapper {...disabledWithTooltip}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          className={buttonStyles({
            color,
            size,
            wrapText,
            width,
            className: cx(
              "inline-flex",
              "cursor-not-allowed opacity-50",
              UNSAFE_className,
            ),
          })}
          aria-disabled={true}
          {...shouldOpenInNewTabAttributes}
        >
          {children}
        </a>
      </DisabledWithTooltipWrapper>
    ) : isPlayAppLink ? (
      <a
        {...props}
        href={href}
        className={buttonStyles({
          color,
          size,
          width,
          wrapText,
          className: cx("inline-flex", UNSAFE_className),
        })}
        {...shouldOpenInNewTabAttributes}
      >
        {children}
      </a>
    ) : (
      <Link href={href}>
        <a
          {...props}
          className={buttonStyles({
            color,
            size,
            width,
            wrapText,
            className: cx("inline-flex", UNSAFE_className),
          })}
          ref={ref}
          {...shouldOpenInNewTabAttributes}
        >
          {children}
        </a>
      </Link>
    );
  },
);

export const openInNewTabHtmlAttributes = {
  target: "_blank",
  // https://medium.com/@jitbit/target-blank-the-most-underestimated-vulnerability-ever-96e328301f4c, https://mathiasbynens.github.io/rel-noopener/, security for older browsers
  rel: "noopener noreferrer",
};
